// @ts-nocheck
import { api } from "../api/client";
import { refreshSession } from "../auth/session";
import { applyHeadstone } from "../map/data-sync";
import { closeViewModal, openViewModal } from "../map/grave-render";
import { mapState } from "../map/state";
import { dtoToLegacy } from "../utils/dto";
import type { LegacyHeadstone } from "../utils/dto";
import { uploadPetMedia, uploadPetPhoto } from "../api/uploads";
import { showToast } from "../components/toast/toast";
import { compressImageToBlob } from "../utils/image";
import { toggleSpeciesOtherField } from "../utils/breeds";
import { bindBreedCombobox } from "../utils/breed-combobox";
import {
  fillPetFormFields,
  readPetFormFields,
  validatePetDates,
} from "../utils/pet-form";
import { t } from "../i18n";
import { translateApiMessage } from "../i18n/api-errors";
import { HEADSTONE_STYLE_KEYS, styleLabelKey } from "../utils/headstone-styles";
import { handleEntitlementError, isPethernityActive, openPaywall } from "./pethernity";

let editingHeadstoneId: string | null = null;
let editFormReady = false;
let pendingGallery = [];

export function resetEditFormBindings() {
  editFormReady = false;
}

export function ensureEditFormReady() {
  if (editFormReady) return;
  const species = document.getElementById("edit-species") as HTMLSelectElement | null;
  const breed = document.getElementById("edit-breed") as HTMLInputElement | null;
  const otherWrap = document.getElementById("edit-species-other-wrap");
  const other = document.getElementById("edit-species-other") as HTMLInputElement | null;
  if (species && breed && otherWrap && other) {
    bindBreedCombobox(species, breed);
    toggleSpeciesOtherField(species, otherWrap, other);
    editFormReady = true;
  }
}

function closeModalsBehindEdit() {
  const myHeadstones = document.getElementById("my-headstones-modal");
  if (myHeadstones) myHeadstones.style.display = "none";
  const accountMenu = document.getElementById("account-menu-modal");
  if (accountMenu) accountMenu.style.display = "none";
}

export function openEditHeadstoneModal(data?: LegacyHeadstone) {
  const legacy = data ?? (window as any).__editingHeadstone;
  if (!legacy?.id) return;

  closeViewModal({ persistGardenLocation: false });
  closeModalsBehindEdit();
  ensureEditFormReady();
  editingHeadstoneId = legacy.id;
  (window as any).__editingHeadstone = legacy;

  fillPetFormFields("edit", {
    name: legacy.name,
    species: legacy.species,
    breed: legacy.breed,
    birthDate: legacy.birthDate,
    deathDate: legacy.deathDate,
    epitaph: legacy.epitaph,
  });

  pendingGallery = Array.isArray(legacy.gallery) ? [...legacy.gallery] : [];
  renderEditStylePicker(legacy.styleKey || "standard");
  renderEditGallery();
  bindEditGalleryInputs();

  const modal = document.getElementById("edit-headstone-modal");
  if (modal) modal.style.display = "flex";
}

function renderEditStylePicker(selected) {
  const picker = document.getElementById("edit-style-picker");
  if (!picker) return;
  picker.dataset.style = selected;
  picker.innerHTML = HEADSTONE_STYLE_KEYS.map(
    (key) => `
      <button type="button" class="style-picker__btn${key === selected ? " is-selected" : ""}" data-style="${key}">
        <span class="style-picker__swatch style-picker__swatch--${key}"></span>
        <span class="style-picker__label">${t(styleLabelKey(key))}</span>
      </button>
    `,
  ).join("");
  picker.querySelectorAll(".style-picker__btn").forEach((btn) => {
    btn.addEventListener("click", () => {
      const style = btn.getAttribute("data-style") || "standard";
      if (style !== "standard" && !isPethernityActive()) {
        openPaywall(t("pethernity.perkStyles"));
        return;
      }
      picker.dataset.style = style;
      picker.querySelectorAll(".style-picker__btn").forEach((el) => {
        el.classList.toggle("is-selected", el.getAttribute("data-style") === style);
      });
    });
  });
}

function renderEditGallery() {
  const host = document.getElementById("edit-gallery-list");
  if (!host) return;
  host.innerHTML = pendingGallery
    .map(
      (item, index) => `
      <button type="button" class="view-gallery__item" data-gallery-index="${index}" title="${t("pethernity.galleryRemove")}">
        ${item.kind === "video" ? "▶" : ""}
        ${item.kind === "photo" ? `<img src="${item.url}" alt="">` : ""}
      </button>
    `,
    )
    .join("");
  host.querySelectorAll("[data-gallery-index]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const index = Number(btn.getAttribute("data-gallery-index"));
      pendingGallery.splice(index, 1);
      renderEditGallery();
    });
  });
}

function bindEditGalleryInputs() {
  const photo = document.getElementById("edit-gallery-photo");
  const video = document.getElementById("edit-gallery-video");
  const cover = document.getElementById("edit-photo-input");
  if (photo && !photo.dataset.bound) {
    photo.dataset.bound = "1";
    photo.addEventListener("change", () => addGalleryFile(photo, "photo"));
  }
  if (video && !video.dataset.bound) {
    video.dataset.bound = "1";
    video.addEventListener("change", () => addGalleryFile(video, "video"));
  }
  if (cover && !cover.dataset.bound) {
    cover.dataset.bound = "1";
    cover.addEventListener("change", () => {
      if (cover.files?.[0] && !isPethernityActive()) {
        cover.value = "";
        openPaywall(t("pethernity.perkPhotos"));
      }
    });
  }
}

async function addGalleryFile(input, kind) {
  const file = input.files?.[0];
  input.value = "";
  if (!file) return;
  if (!isPethernityActive()) {
    openPaywall(t("pethernity.perkPhotos"));
    return;
  }
  const photos = pendingGallery.filter((i) => i.kind === "photo").length;
  const videos = pendingGallery.filter((i) => i.kind === "video").length;
  if (kind === "photo" && photos >= 8) {
    showToast(t("api.galleryPhotoLimit"));
    return;
  }
  if (kind === "video" && videos >= 2) {
    showToast(t("api.galleryVideoLimit"));
    return;
  }
  try {
    const blob =
      kind === "photo" ? await compressImageToBlob(file, 800, 0.8) : file;
    const { objectName } = await uploadPetMedia(blob, kind);
    pendingGallery.push({
      url: URL.createObjectURL(file),
      objectName,
      kind,
    });
    renderEditGallery();
  } catch (err) {
    if (!handleEntitlementError(err, t("pethernity.perkPhotos"))) {
      showToast(t("grave.uploadFailed", { message: err?.message ?? err }));
    }
  }
}

export function closeEditHeadstoneModal() {
  const modal = document.getElementById("edit-headstone-modal");
  if (modal) modal.style.display = "none";
  editingHeadstoneId = null;
}

export async function submitEditHeadstone() {
  if (!editingHeadstoneId) return;

  const user = await refreshSession();
  if (!user) {
    window.openAuthModal("login", t("auth.loginForEdit"));
    return;
  }

  const f = readPetFormFields("edit");
  if (!f.name) {
    showToast(t("grave.nameRequired"));
    return;
  }
  const dateErr = validatePetDates(f.birthDate ?? "", f.deathDate ?? "");
  if (dateErr) {
    showToast(`⚠️ ${dateErr}`);
    return;
  }

  const pet: Record<string, string | null> = {
    name: f.name,
    species: f.species ?? null,
    breed: f.breed ?? null,
    birthDate: f.birthDate || null,
    deathDate: f.deathDate || null,
  };

  if (f.inpPhoto?.files && f.inpPhoto.files[0]) {
    if (!isPethernityActive(user)) {
      openPaywall(t("pethernity.perkPhotos"));
      return;
    }
    try {
      const blob = await compressImageToBlob(f.inpPhoto.files[0], 800, 0.8);
      const { objectName } = await uploadPetPhoto(blob);
      pet.imageObjectName = objectName;
      pet.imageMime = "image/jpeg";
    } catch (e: any) {
      console.error("Photo upload error:", e);
      if (!handleEntitlementError(e, t("pethernity.perkPhotos"))) {
        showToast(t("grave.uploadFailed", { message: e?.message ?? e }));
      }
      return;
    }
  }

  const styleKey =
    document.getElementById("edit-style-picker")?.dataset.style || "standard";
  if (styleKey !== "standard" && !isPethernityActive(user)) {
    openPaywall(t("pethernity.perkStyles"));
    return;
  }

  try {
    const { data, error } = await api.PUT("/headstones/{id}", {
      params: { path: { id: editingHeadstoneId } },
      body: {
        epitaph: f.epitaph || null,
        styleKey,
        pet: {
          ...pet,
          ...(isPethernityActive(user)
            ? {
                galleryUrls: pendingGallery.map((item) => ({
                  url: item.url,
                  objectName: item.objectName,
                  kind: item.kind,
                })),
              }
            : {}),
        },
      },
    });

    if (error || !data) {
      if (handleEntitlementError(error, t("pethernity.required"))) return;
      const msg = translateApiMessage((error as any)?.message) ?? t("grave.unexpectedError");
      showToast(`❌ ${msg}`);
      return;
    }

    applyHeadstone(data);
    const legacy = dtoToLegacy(data);

    closeEditHeadstoneModal();

    const holeEl = mapState.holeEls[legacy.holeId];
    if (holeEl) openViewModal(holeEl);
    else closeViewModal();

    showToast(t("editHeadstone.updated"));
  } catch (err: any) {
    console.error("Update headstone failed", err);
    showToast(t("editHeadstone.error", { message: err?.message ?? err }));
  }
}
